import { selectorFamily, atom, selector } from 'recoil'
import { userClient } from 'src/clients'
import { UserSystemMetaBase, UserWithMeta, UserTier, User, USER_TIERS } from 'src/common-services'
import { authData } from 'src/components/auth/util'
import { keyFactory } from 'src/utils/recoil'

const key = keyFactory('user')

export const userRefreshStamp = atom<Date>({
  key: key('refresh-stamp'),
  default: new Date(),
})

export type { UserTier, UserSystemMetaBase }

const ONE_YEAR_IN_MS = new Date().getTime() + 365 * 24 * 60 * 60 * 1000

export const mockUser = atom<UserWithMeta | undefined>({
  key: key('mock-user'),
  default: undefined,
})

export const userWithMeta = selectorFamily<UserWithMeta, User['id']>({
  key: key('user-with-meta'),
  get:
    (id: User['id']) =>
    async ({ get }) => {
      if (process.env.NODE_ENV === 'development') {
        const mock = get(mockUser)
        if (mock) {
          return mock
        }
      }
      get(userRefreshStamp)
      const user = await userClient.getUser({ id })

      const result: UserWithMeta = {
        ...user,
        expires: new Date(user.expires || ONE_YEAR_IN_MS),
      }
      return result
    },
})

export const currentUser = selector<UserWithMeta | undefined>({
  key: key('current-user'),
  get: async ({ get }) => {
    if (process.env.NODE_ENV === 'development') {
      const mock = get(mockUser)
      if (mock) {
        return mock
      }
    }
    const authData = await userClient.current()
    return authData
  },
})

export const isPaidUser = selector({
  key: key('is-premium'),
  get: ({ get }) => {
    const tier = get(currentUser)?.tier
    return tier !== 'free'
  },
})

export const isTrial = selector({
  key: key('is-trial'),
  get: ({ get }) => {
    const tier = get(currentUser)?.tier
    return !tier || tier === 'trial'
  },
})

export const meetsTier = selectorFamily<boolean, UserTier | undefined>({
  key: key('meets-tier'),
  get:
    (tier) =>
    ({ get }) => {
      const user = get(authData)
      const tierIndex = USER_TIERS.findIndex((t) => t === tier)
      const userTierIndex = USER_TIERS.findIndex((t) => t === user?.tier)
      return userTierIndex >= tierIndex
    },
})

export const userCanEditPassword = selector({
  key: key('user-can-edit-password'),
  get: ({ get }) => {
    const user = get(currentUser)
    if (!user) {
      return false
    }

    const signupMethod = user.signupMethod
    // old users don't have signupMethod set, so we assume they are admin or users(custumers) using email to login
    return !signupMethod || signupMethod === 'admin' || signupMethod === 'desktop.email'
  },
})
